import axios from "axios";

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

// One queue for searches and topics, including StrictMode remounts.
export function createNewsdataClient({ gap = 2000, cacheMs = 300000, retries = 2 } = {}) {
  let queue = Promise.resolve();
  let nextRequestAt = 0;
  const pending = new Map();
  const cache = new Map();

  return function fetchNews(url, isActive = () => true) {
    const cached = cache.get(url);
    if (cached && cached.expires > Date.now()) return Promise.resolve(cached.response);
    if (pending.has(url)) {
      const entry = pending.get(url);
      entry.consumers.push(isActive);
      return entry.promise;
    }
    const entry = { consumers: [isActive] };
    const run = async () => {
      for (let attempt = 0; ; attempt += 1) {
        const wait = nextRequestAt - Date.now();
        if (wait > 0) await sleep(wait);
        if (!entry.consumers.some(active => active())) {
          throw new Error("News request cancelled.");
        }
        try {
          const response = await axios.get(url);
          if (response?.data?.status === "error") {
            const error = new Error(response.data.results?.message || "NewsData.io request failed.");
            error.response = response;
            throw error;
          }
          const results = response?.data?.results;
          if (!Array.isArray(results)) throw new Error("Invalid NewsData.io response.");
          const articles = results.map(item => ({
            title: item.title,
            url: item.link,
            description: item.description,
            image: item.image_url,
            author: Array.isArray(item.creator) ? item.creator.join(", ") : item.creator || item.source_name || "",
          }));
          const normalized = { ...response, data: { data: articles } };
          cache.set(url, { response: normalized, expires: Date.now() + cacheMs });
          return normalized;
        } catch (error) {
          const providerError = error.response?.data?.results;
          if (providerError?.message) error.message = providerError.message;
          const quota = ["CreditsExhausted", "DailyLimitExceeded"].includes(providerError?.code);
          const limited = error.response?.status === 429 || providerError?.code === "RateLimitExceeded";
          if (quota) {
            error.message = "NewsData.io's request quota is exhausted. Check your account usage.";
            throw error;
          }
          if (!limited) throw error;
          const retryAfter = error.response?.headers?.["retry-after"];
          const seconds = Number(retryAfter);
          const retryDelay = retryAfter != null && Number.isFinite(seconds)
            ? seconds * 1000
            : Date.parse(retryAfter) - Date.now();
          nextRequestAt = Date.now() + Math.max(gap, Number.isFinite(retryDelay) ? retryDelay : 5000 * 2 ** attempt);
          if (attempt >= retries) {
            error.message = "The news provider is limiting requests. Please try again shortly.";
            throw error;
          }
        } finally {
          nextRequestAt = Math.max(nextRequestAt, Date.now() + gap);
        }
      }
    };
    entry.promise = queue.then(run).finally(() => pending.delete(url));
    pending.set(url, entry);
    queue = entry.promise.catch(() => {});
    return entry.promise;
  };
}

export const fetchNewsdata = createNewsdataClient();
